import { useState, CSSProperties } from 'react';
import useScreen from 'Hooks/useScreen';
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils';
import { PlanType, DEFAULT_CURRENCY } from 'modules/Const';
import { clsx, styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import { upgradePlanAndRedirectToCheckout_api } from 'api/subscriptionApi';
import { PaymentMethods } from 'modules/Types';
import { ReactComponent as ChevronDown } from 'assets/images/chevron-down-arrow-icon.svg';
import PublicPricingTile from './PublicPricingTile';
import PublicCurrencyDropdown from './PublicCurrencyDropdown';
import BillingCycleToggle from 'components/shareable/BillingCycleToggle';

interface FAQItem {
    question: string;
    answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
    {
        question: "Is SymphonyOS right for me?",
        answer: "Symphony has been used by all kinds of creators & artists, brands - from independent creators & artists releasing their first song and looking for help with Maestro, to the teams behind Mariah Carey, BigXthaPlug, and even marketing agencies using Symphony to automate marketing for their clients. Whether you're just starting out or managing multiple brands, Symphony scales with your needs."
    },
    {
        question: "Is there a free version of Symphony?",
        answer: "Our cheapest plan starts at $15/month and includes unlimited websites, AI-powered marketing tools, social analytics, and paid ads. Our affiliate program is a way for many people to offset costs - we've already paid out over $50K to folks who have referred people to Symphony."
    },
   
    {
        question: "What counts as an AI message?",
        answer: "Anything Maestro outputs on your behalf, such as Maestro-built campaigns, Maestro-built link-in-bio pages, Maestro-built reports, Maestro marketing plans or content strategies, and generating images and videos. You can chat with Maestro for free - basic conversations won't count against your message limit."
    },
    {
        question: "How is Symphony different from other marketing platforms?",
        answer: "Symphony combines everything you need in one platform: AI-powered marketing automation, unlimited website building, cross-platform analytics, fan CRM, and paid advertising - all powered by Maestro, our AI assistant trained on 10,000s of successful campaigns. Instead of juggling multiple tools, you get a complete marketing ecosystem."
    },
    {
        question: "Do I need technical skills to use Symphony?",
        answer: "Not at all! Symphony is designed for creators, not developers. Maestro can handle the technical work for you - from building campaigns to creating websites. You can also use our manual tools if you prefer hands-on control. Most users start with Maestro and gradually explore the advanced features."
    },
    {
        question: "Can I use Symphony for multiple creators & artists or brands?",
        answer: "Yes! Our Team plan is specifically designed for agencies, managers, and teams working with multiple brands. You can manage unlimited brands, collaborate with team members, and access advanced reporting features. Many marketing agencies use Symphony to automate client campaigns."
    },
    {
        question: "What kind of results can I expect?",
        answer: "Results vary by genre, audience, and goals, but our users typically see improved engagement rates, better audience insights, and significant time savings. Maestro is trained on successful campaigns across thousands of creators & artists, so you benefit from proven strategies and best practices."
    },
    {
        question: "Can I switch plans at any time?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
    },
    {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards through Stripe, our secure payment processor."
    },
    {
        question: "What happened to the Free, Lite, and Pro tiers?",
        answer: "Before August, we previously had Free, Lite ($6/month), and Pro ($20/month) tiers. While we would've loved to keep those tiers and continue offering Symphony at a cheaper rate, business realities meant we had to introduce new pricing tiers that represent the value provided with Maestro and continue fueling further development. Our current plans are Starter ($15/month) and Growth ($39/month). Anyone who subscribed to Lite or Pro before August 1st will be grandfathered into their old pricing through December 31, 2025, as long as their subscription remains active."
    },
    {
        question: "Do you offer refunds?",
        answer: "For sure, if you're not satisfied with Symphony, please reach out within 48-hours of being charged your subscription and our team will be happy to help you out. NOTE: this does not apply to payments for marketing campaigns."
    },
    
];

const Pricing = () => {
    const { mobileView, mStyle } = useScreen();
    const [billingCycle, setBillingCycle] = useState(BillingCycle.MONTHLY);
    const [currencyCode, setCurrencyCode] = useState(DEFAULT_CURRENCY);
    const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
    const [isCheckingOut, setIsCheckingOut] = useState<PlanType | null>(null);

    const handleBillingCycleToggle = (cycle: BillingCycle) => {
        setBillingCycle(cycle);
    };

    const handleGetStarted = async (planType: PlanType) => {
        setIsCheckingOut(planType);
        try {
            await upgradePlanAndRedirectToCheckout_api({
                recurring_interval: billingCycle,
                plan_type: planType,
                brandSlug: '', // Will be handled by the checkout process
                payment_method: PaymentMethods.STRIPE,
                checkout_source: 'Public Pricing Page' as any,
            });
        } catch (error) {
            console.error('Error starting checkout:', error);
        } finally {
            setIsCheckingOut(null);
        }
    };

    const toggleFAQ = (index: number) => {
        setExpandedFAQ(expandedFAQ === index ? null : index);
    };

    const renderTitle = () => {
        return (
            <div className="pricingTitleContainer" style={sty.pricingTitleContainer}>
                <div
                    className="pricingTitle"
                    style={mStyle(sty, 'pricingTitle')}
                >
                    All-in-one AI-powered growth tools for creators
                </div>
                <div
                    className="pricingSubtitle"
                    style={mStyle(sty, 'pricingSubtitle')}
                >
                    Automate your marketing, grow your audience, and scale your business with AI.
                </div>
            </div>
        );
    };

    const renderBillingCycleToggle = () => {
        return (
            <BillingCycleToggle
                className="pricingBillingToggle"
                billingCycle={billingCycle}
                onBillingCycleChange={handleBillingCycleToggle}
                style={mStyle(sty, 'pricingBillingToggleContainer')}
            />
        );
    };

    const renderPricingTiles = () => {
        const plans = [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2];
        
        return (
            <div
                className="pricingTilesContainer"
                style={mStyle(sty, 'pricingTilesContainer')}
            >
                {plans.map((plan, index) => {
                    const isMostPopular = plan === PlanType.GROWTH;
                    
                    return (
                        <PublicPricingTile
                            key={plan}
                            planType={plan}
                            billingCycle={billingCycle}
                            currencyCode={currencyCode}
                            isMostPopular={isMostPopular}
                            onGetStarted={() => handleGetStarted(plan)}
                            isLoading={isCheckingOut === plan}
                        />
                    );
                })}
            </div>
        );
    };

    const renderSocialProof = () => {
        return (
            <div className="pricingSocialProof" style={sty.pricingSocialProof}>
                <div
                    className="socialProofText"
                    style={mStyle(sty, 'socialProofText')}
                >
                    Join 100K+ brands & artists in centralizing your marketing
                </div>
                <div
                    className="socialProofSubtext"
                    style={mStyle(sty, 'socialProofSubtext')}
                >
                    Trusted by creators, labels, and agencies worldwide
                </div>
            </div>
        );
    };

    const renderFAQs = () => {
        return (
            <div className="pricingFAQsSection" style={sty.pricingFAQsSection}>
                <div
                    className="pricingFAQsTitle"
                    style={mStyle(sty, 'pricingFAQsTitle')}
                >
                    Frequently Asked Questions
                </div>
                <div className="pricingFAQsList" style={sty.pricingFAQsList}>
                    {FAQ_ITEMS.map((faq, index) => {
                        const isExpanded = expandedFAQ === index;
                        return (
                            <div
                                key={index}
                                className="pricingFAQItem"
                                style={sty.pricingFAQItem}
                            >
                                <div
                                    className={clsx(
                                        'pricingFAQQuestion',
                                        { 'pricingFAQQuestionExpanded': isExpanded }
                                    )}
                                    style={sty.pricingFAQQuestion}
                                    onClick={() => toggleFAQ(index)}
                                >
                                    <span>{faq.question}</span>
                                    <ChevronDown
                                        style={{
                                            ...sty.pricingFAQChevron,
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                    />
                                </div>
                                <div
                                    className="pricingFAQAnswerContainer"
                                    style={{
                                        ...sty.pricingFAQAnswerContainer,
                                        maxHeight: isExpanded ? '500px' : '0px',
                                        opacity: isExpanded ? 1 : 0,
                                        padding: isExpanded ? '20px 24px 20px 24px' : '0 24px 0 24px',
                                        borderTop: isExpanded ? `1px solid ${Colors.DARK_DIVIDER_LINE}` : 'none',
                                    }}
                                >
                                    <div
                                        className="pricingFAQAnswer"
                                        style={sty.pricingFAQAnswer}
                                    >
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div
            className={clsx('pricingPageComponent')}
            style={mStyle(sty, 'pricingPageComponent')}
        >
            {renderTitle()}
            
            <div className="pricingControlsContainer" style={mStyle(sty, 'pricingControlsContainer')}>
                {!mobileView && <div style={{ flex: 1 }}></div>}
                {renderBillingCycleToggle()}
                <div style={mobileView ? {} : { flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <PublicCurrencyDropdown
                        className="pricingCurrencyDropdown"
                        initialCurrency={currencyCode}
                        onCurrencyChange={setCurrencyCode}
                        hideTitle={true}
                    />
                </div>
            </div>

            {renderPricingTiles()}
            {/* {renderSocialProof()} */}
            {renderFAQs()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    pricingPageComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 1100,
        padding: '64px 24px',
        minHeight: '100vh',
    },
    pricingPageComponent_mobile: {
        gap: 16,
        padding: '32px 16px',
    },
    pricingTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        textAlign: 'center',
    },
    pricingTitle: {
        fontSize: 48,
        fontWeight: 600,
        color: Colors.textColor,
        lineHeight: 1.2,
    },
    pricingTitle_mobile: {
        fontSize: 32,
        fontWeight: 600,
    },
    pricingSubtitle: {
        fontSize: 20,
        color: Colors.greyDark,
        lineHeight: 1.4,
    },
    pricingSubtitle_mobile: {
        fontSize: 16,
    },
    pricingControlsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    pricingControlsContainer_mobile: {
        flexDirection: 'column',
        gap: 24,
        alignItems: 'stretch',
    },
    pricingBillingToggleContainer: {
        width: 'fit-content',
    },
    pricingBillingToggleContainer_mobile: {
        width: '100%',
        alignSelf: 'stretch',
    },
    pricingTilesContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    pricingTilesContainer_mobile: {
        flexDirection: 'column',
        alignItems: 'center',
    },
    pricingSocialProof: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'center',
        padding: '48px 24px',
        backgroundColor: Colors.purpleLightest,
        borderRadius: 16,
        marginBottom: 32,
    },
    socialProofText: {
        fontSize: 28,
        fontWeight: 600,
        color: Colors.textColor,
        lineHeight: 1.3,
    },
    socialProofText_mobile: {
        fontSize: 22,
    },
    socialProofSubtext: {
        fontSize: 16,
        color: Colors.greyDark,
        lineHeight: 1.4,
    },
    socialProofSubtext_mobile: {
        fontSize: 14,
    },
    pricingFAQsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        marginTop: 32,
    },
    pricingFAQsTitle: {
        fontSize: 32,
        fontWeight: 600,
        color: Colors.textColor,
        textAlign: 'center',
    },
    pricingFAQsTitle_mobile: {
        fontSize: 24,
    },
    pricingFAQsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 800,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
    },
    pricingFAQItem: {
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: Colors.white,
    },
    pricingFAQQuestion: {
        padding: '20px 24px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 16,
        fontWeight: 500,
        color: Colors.textColor,
        backgroundColor: Colors.white,
        transition: 'all 0.3s ease',
    },

    pricingFAQAnswerContainer: {
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        backgroundColor: Colors.white,
    },
    pricingFAQAnswer: {
        fontSize: 14,
        color: Colors.textColor,
        lineHeight: 1.5,
    },
    pricingFAQChevron: {
        width: 16,
        height: 16,
        transition: 'transform 0.3s ease',
        flexShrink: 0,
        fill: Colors.purple,
    },
};

export default Pricing; 